import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { SaveBar } from "../../utils/SaveBar";
import { fetchHseSettings, updateHseSettings } from "../../../api/hse/settings";
import { getDisciplines } from "../../../api/hse/disciplines";
import { HseFieldInfoLabel } from "./HseFieldInfoLabel";
import {
  normalizeHseAiSettings,
  snapshotHseAiSettings,
  toHseAiPayload,
} from "./hseCompanySettingsUtils";

export function HseAiSettingsForm({ onSaved }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [settings, setSettings] = useState(normalizeHseAiSettings({}));
  const [savedSnapshot, setSavedSnapshot] = useState(snapshotHseAiSettings({}));
  const [disciplines, setDisciplines] = useState([]);

  const applyLoaded = useCallback((data) => {
    const next = normalizeHseAiSettings(data);
    setSettings(next);
    setSavedSnapshot(snapshotHseAiSettings(next));
    onSaved?.(data);
  }, [onSaved]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [data, disciplineResponse] = await Promise.all([
          fetchHseSettings(),
          getDisciplines().catch(() => ({ company_disciplines: [] })),
        ]);
        if (cancelled) return;
        applyLoaded(data);
        const company = disciplineResponse.company_disciplines || [];
        const templates = disciplineResponse.templates || [];
        setDisciplines(company.length ? company : templates);
      } catch {
        if (!cancelled) toast.error(t("hse.settings.load_failed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyLoaded, t]);

  const hasChanges = useMemo(
    () => snapshotHseAiSettings(settings) !== savedSnapshot,
    [settings, savedSnapshot]
  );

  const patch = (next) => {
    setSaveSuccess(false);
    setSaveError(false);
    setSettings((prev) => ({ ...prev, ...next }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(false);
    try {
      const data = await updateHseSettings(toHseAiPayload(settings));
      applyLoaded(data);
      setSaveSuccess(true);
      toast.success(t("hse.settings.saved"));
      return true;
    } catch {
      setSaveError(true);
      toast.error(t("hse.settings.save_failed"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ResourceLoading />;

  return (
    <>
      <div className="mx-auto max-w-3xl space-y-4 pb-28">
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-4">
            <HseFieldInfoLabel
              label="hse.settings.ai.title"
              infoTitle="hse.settings.ai.info_title"
              infoBody="hse.settings.ai.info_body"
            />
            <p className="mt-1 text-sm text-slate-600">{t("hse.settings.ai.description")}</p>
          </div>
          <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50/50 p-3">
            <input
              type="checkbox"
              checked={settings.suggestions_enabled === true}
              onChange={(event) => patch({ suggestions_enabled: event.target.checked })}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
            />
            <div>
              <div className="text-sm font-medium text-slate-900">
                {t("hse.settings.ai.enable")}
              </div>
              <p className="mt-0.5 text-xs text-slate-500">{t("hse.settings.ai.enable_help")}</p>
              {settings.suggestions_enabled ? (
                <p className="mt-1 text-xs text-slate-500">
                  {settings.configured
                    ? t("hse.settings.ai.configured_ok")
                    : t("hse.settings.ai.configured_missing")}
                </p>
              ) : null}
            </div>
          </label>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h3 className="text-base font-semibold text-slate-900">
            {t("hse.settings.ai.discipline_title")}
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            {t("hse.settings.ai.discipline_description")}
          </p>
          <select
            value={settings.default_discipline_id || ""}
            onChange={(event) =>
              patch({
                default_discipline_id: event.target.value ? Number(event.target.value) : null,
              })
            }
            className="mt-4 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
          >
            <option value="">{t("hse.settings.ai.discipline_none")}</option>
            {disciplines.map((item) => (
              <option key={item.id || item.code} value={item.id || ""}>
                {item.name || item.code}
              </option>
            ))}
          </select>
        </section>
      </div>

      <SaveBar
        hasChanges={hasChanges}
        isSaving={saving}
        isSuccess={saveSuccess}
        isError={saveError}
        successMessage={t("hse.settings.saved")}
        errorMessage={t("hse.settings.save_failed")}
        onSave={handleSave}
        saveLabel={t("save")}
      />
    </>
  );
}
